import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const ProductImporter = ({ open, onOpenChange, language = 'es', onImportComplete }) => {
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const pollIntervalRef = useRef(null);

  const t = {
    es: {
      title: 'Importar Productos desde Excel',
      description: 'Sube un archivo Excel (.xlsx) con los productos a importar',
      selectFile: 'Seleccionar Archivo',
      replaceAll: 'Reemplazar todos los productos existentes',
      replaceWarning: 'Esta opción eliminará todos los productos actuales antes de importar',
      downloadTemplate: 'Descargar Plantilla',
      import: 'Importar',
      importing: 'Importando...',
      success: 'Importación completada',
      imported: 'productos importados',
      errors: 'errores',
      cancel: 'Cancelar',
      close: 'Cerrar',
      noFile: 'Por favor selecciona un archivo',
      processing: 'Procesando...',
      pending: 'Iniciando importación...',
      completed: 'Completado',
      failed: 'Error en la importación',
    },
    en: {
      title: 'Import Products from Excel',
      description: 'Upload an Excel file (.xlsx) with products to import',
      selectFile: 'Select File',
      replaceAll: 'Replace all existing products',
      replaceWarning: 'This option will delete all current products before importing',
      downloadTemplate: 'Download Template',
      import: 'Import',
      importing: 'Importing...',
      success: 'Import completed',
      imported: 'products imported',
      errors: 'errors',
      cancel: 'Cancel',
      close: 'Close',
      noFile: 'Please select a file',
      processing: 'Processing...',
      pending: 'Starting import...',
      completed: 'Completed',
      failed: 'Import failed',
    }
  };

  const texts = t[language] || t.es;

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const pollImportStatus = useCallback(async (jId) => {
    try {
      const response = await axios.get(`${API}/admin/products/import-status/${jId}`);
      const data = response.data;
      
      setProgress(data.progress || 0);
      
      if (data.status === 'processing') {
        setStatusMessage(`${texts.processing} ${data.imported || 0}/${data.total_rows || '?'}`);
      } else if (data.status === 'pending') {
        setStatusMessage(texts.pending);
      } else if (data.status === 'completed') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setImporting(false);
        setImportResult({
          imported: data.imported,
          errors: data.errors,
          error_details: data.error_details,
          total_rows: data.total_rows
        });
        toast.success(`${texts.success}: ${data.imported} ${texts.imported}`);
        if (onImportComplete) {
          onImportComplete();
        }
      } else if (data.status === 'failed') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setImporting(false);
        setImportResult({ error: data.error || texts.failed });
        toast.error(data.error || texts.failed);
      }
    } catch (error) {
      console.error('Error polling import status:', error);
    }
  }, [texts, onImportComplete]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      setProgress(0);
      setJobId(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error(texts.noFile);
      return;
    }

    setImporting(true);
    setImportResult(null);
    setProgress(0);
    setStatusMessage(texts.pending);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Start async import
      const response = await axios.post(
        `${API}/admin/products/import-async?replace_all=${replaceAll}`, 
        formData, 
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // 1 minute for initial upload
        }
      );

      const jId = response.data.job_id;
      setJobId(jId);
      
      // Start polling for status
      pollIntervalRef.current = setInterval(() => {
        pollImportStatus(jId);
      }, 2000); // Poll every 2 seconds
      
    } catch (error) {
      let errorMsg = 'Error importing';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = language === 'es' 
          ? 'Error al subir el archivo. Intenta con un archivo más pequeño.'
          : 'Error uploading file. Try with a smaller file.';
      } else {
        errorMsg = error.response?.data?.detail || error.message || 'Error importing';
      }
      toast.error(errorMsg);
      setImportResult({ error: errorMsg });
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error downloading template');
    }
  };

  const handleClose = () => {
    // Stop polling if active
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setSelectedFile(null);
    setImportResult(null);
    setReplaceAll(false);
    setProgress(0);
    setJobId(null);
    setStatusMessage('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {texts.title}
          </DialogTitle>
          <DialogDescription>{texts.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Input */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              <Upload className="h-4 w-4 mr-2" />
              {selectedFile ? selectedFile.name : texts.selectFile}
            </Button>
          </div>

          {/* Replace All Option */}
          <div className="flex items-start space-x-2 p-3 border rounded bg-amber-50 border-amber-200">
            <Checkbox
              id="replaceAll"
              checked={replaceAll}
              onCheckedChange={setReplaceAll}
              disabled={importing}
            />
            <div className="space-y-1">
              <Label htmlFor="replaceAll" className="text-sm font-medium cursor-pointer">
                {texts.replaceAll}
              </Label>
              <p className="text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {texts.replaceWarning}
              </p>
            </div>
          </div>

          {/* Template Download */}
          <Button variant="link" size="sm" className="p-0" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-1" />
            {texts.downloadTemplate}
          </Button>

          {/* Import Progress/Result */}
          {importing && (
            <div className="space-y-2">
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
                {statusMessage || texts.importing}
              </p>
              <p className="text-xs text-center text-green-600 font-medium">
                {progress}%
              </p>
              <p className="text-xs text-center text-amber-600">
                {language === 'es' 
                  ? 'La importación continúa en segundo plano. Puedes esperar aquí o cerrar este diálogo.'
                  : 'Import continues in background. You can wait here or close this dialog.'}
              </p>
            </div>
          )}

          {importResult && !importResult.error && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-700">{texts.success}</AlertTitle>
              <AlertDescription className="text-green-600">
                {importResult.imported} {texts.imported}
                {importResult.errors > 0 && ` • ${importResult.errors} ${texts.errors}`}
              </AlertDescription>
            </Alert>
          )}

          {importResult?.error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{importResult.error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={importing}>
            {importResult ? texts.close : texts.cancel}
          </Button>
          {!importResult && (
            <Button onClick={handleImport} disabled={importing || !selectedFile}>
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {texts.importing}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {texts.import}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductImporter;
